import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ARTEMIS_OB_SYNC_REQUIRED_MESSAGE,
  checkArtemisAccess,
  type ArtemisAccessResult
} from '@/services/artemisAccessService';
import { openArtemisWindow } from '@/utils/windowOpenHelper';

const ARTEMIS_ACCESS_TTL_MS = 60_000;

type ArtemisAccess =
  | { status: 'unknown' | 'loading' | 'error' }
  | { status: 'allowed' | 'denied'; checkedAt: number };

interface UseArtemisLauncherOptions {
  enabled?: boolean;
  openWindow?: (path?: string) => void;
  userKey: string;
}

/**
 * Owns the portal's cached Artemis eligibility check so every launcher keeps
 * the same pre-open denial, retry, and protected-route behavior.
 */
export function useArtemisLauncher({
  enabled = true,
  openWindow = openArtemisWindow,
  userKey
}: UseArtemisLauncherOptions) {
  const [access, setAccess] = useState<ArtemisAccess>({ status: 'unknown' });
  const accessSeqRef = useRef(0);

  const refreshAccess = useCallback(async () => {
    if (!enabled) {
      return;
    }

    const seq = accessSeqRef.current + 1;
    accessSeqRef.current = seq;
    setAccess((current) => (
      current.status === 'allowed' || current.status === 'denied'
        ? current
        : { status: 'loading' }
    ));

    let result: ArtemisAccessResult;
    try {
      result = await checkArtemisAccess();
    } catch {
      result = { status: 'error' };
    }

    if (accessSeqRef.current !== seq) {
      return;
    }
    if (result.status === 'allowed' || result.status === 'denied') {
      setAccess({ status: result.status, checkedAt: Date.now() });
    } else {
      setAccess({ status: 'error' });
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    setAccess({ status: 'unknown' });
    void refreshAccess();
  }, [enabled, refreshAccess, userKey]);

  // `path` opens a specific Artemis view, such as Online Leads.
  return useCallback((path?: string) => {
    if (!enabled) {
      return;
    }

    const cacheFresh = (access.status === 'allowed' || access.status === 'denied')
      && Date.now() - access.checkedAt < ARTEMIS_ACCESS_TTL_MS;

    if (cacheFresh && access.status === 'denied') {
      toast.error(ARTEMIS_OB_SYNC_REQUIRED_MESSAGE, {
        duration: 10000,
        action: {
          label: 'Retry',
          onClick: () => {
            void refreshAccess();
          }
        }
      });
      return;
    }

    if (!cacheFresh) {
      void refreshAccess();
    }
    if (path) {
      openWindow(path);
    } else {
      openWindow();
    }
  }, [access, enabled, openWindow, refreshAccess]);
}
